import { useCallback, useMemo } from 'react';
import {
  type DboConfigSection,
  type DboLinkItem,
  type DboNavContext,
  type DboNavItem,
  type DboParentItem,
} from './types';
import { DBO_ROUTE_CONFIG } from './constants';

import { useAbility } from '@cast/shared-ability-data-access';
import { PRODUCT_BASE_PATHS } from '@cast/console-constants';
import { matchPath, useLocation } from 'react-router-dom';
import { buildDboRoute } from 'features/db-optimization/v2/routes';

type GetVisibleDboNavItemsParams = {
  items: DboNavItem[];
  ctx: DboNavContext;
  can: ReturnType<typeof useAbility>['can'];
};

export const isDboParentRoute = (item: DboNavItem): item is DboParentItem => {
  return (item as DboParentItem).children !== undefined;
};

export const validateRoutePermissions = (
  rules: DboLinkItem['rules'],
  can: ReturnType<typeof useAbility>['can']
): boolean => {
  return !rules || rules.every(([action, subject]) => can(action, subject));
};

export const createDboNavItemFilter =
  ({ can, ctx }: Omit<GetVisibleDboNavItemsParams, 'items'>) =>
  ({ condition, routeOnly, rules }: DboNavItem): boolean =>
    !routeOnly &&
    (!condition || condition(ctx)) &&
    validateRoutePermissions(rules, can);

export const getVisibleDboNavItems = ({
  can,
  ctx,
  items,
}: GetVisibleDboNavItemsParams): DboNavItem[] => {
  const dboNavItemFilter = createDboNavItemFilter({ can, ctx });

  return items.flatMap((item): DboNavItem[] => {
    if (!dboNavItemFilter(item)) {
      return [];
    }

    if (isDboParentRoute(item)) {
      const children = item.children.filter(dboNavItemFilter);
      return children.length > 0 ? [{ ...item, children }] : [];
    }

    return [item];
  });
};

export const flattenNavItems = (items: DboNavItem[]): DboLinkItem[] =>
  items.flatMap((item) => (isDboParentRoute(item) ? item.children : [item]));

export const useGetDboRoutes = (): DboLinkItem[] => {
  const { can } = useAbility();
  return useMemo(
    () =>
      [
        ...flattenNavItems(DBO_ROUTE_CONFIG.organization),
        ...flattenNavItems(DBO_ROUTE_CONFIG.server),
        ...flattenNavItems(DBO_ROUTE_CONFIG.database),
      ].filter(({ rules }) => validateRoutePermissions(rules, can)),
    [can]
  );
};

const DBO_BREADCRUMB_MAP = new Map(
  flattenNavItems([
    ...DBO_ROUTE_CONFIG.organization,
    ...DBO_ROUTE_CONFIG.server,
    ...DBO_ROUTE_CONFIG.database,
  ]).map((item) => [item.route, item.breadcrumbs])
);

export const matchDboBreadcrumbs = (pathname: string): string[] | undefined => {
  for (const routePattern of DBO_BREADCRUMB_MAP.keys()) {
    const match = matchPath(
      `${PRODUCT_BASE_PATHS.dbo}/${routePattern}`,
      pathname
    );
    if (match) {
      return DBO_BREADCRUMB_MAP.get(routePattern);
    }
  }

  return undefined;
};

export const getFirstVisibleChild = (
  item: DboParentItem
): DboLinkItem | undefined => item.children.find((c) => !c.routeOnly);

export const isParentRouteActive = (
  item: DboParentItem,
  params: Record<string, string | number>,
  isPathActive: (path: string) => boolean
): boolean =>
  item.children.some(
    (c) => !c.routeOnly && isPathActive(buildDboRoute(c.route, params))
  );

export const useDboPathActive = (): ((path: string) => boolean) => {
  const { pathname } = useLocation();
  return useCallback(
    (path: string) =>
      !!path && (pathname === path || pathname.startsWith(`${path}/`)),
    [pathname]
  );
};

export const useVisibleDboNavItems = (
  section: DboConfigSection,
  { isOptimized, isManualProvider }: DboNavContext
): DboNavItem[] => {
  const { can } = useAbility();
  return useMemo(
    () =>
      getVisibleDboNavItems({
        items: DBO_ROUTE_CONFIG[section],
        ctx: { isOptimized, isManualProvider },
        can,
      }),
    [can, section, isOptimized, isManualProvider]
  );
};
